import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { IMG } from "../data/images";
import { CheckCircle2, ArrowRight } from "lucide-react";

export default function About() {
  return (
    <>
      <SEO
        title="About Cochlear Implants — How They Work, Who Benefits"
        description="Complete medical guide to cochlear implants in India. Learn how a cochlear implant works, the difference from hearing aids, who benefits, and what to expect."
      />
      <section className="bg-gradient-to-br from-[#F0F9FF] via-white to-[#ECFDF5] py-14">
        <div className="hc-container max-w-4xl">
          <h1 className="font-serif text-[2.25rem] md:text-[3rem] font-semibold leading-[1.08] text-slate-900">
            About Cochlear Implants
          </h1>
          <p className="mt-5 text-slate-700 text-[1.08rem]">
            Everything families and adults need to understand about cochlear implants —
            in clear, parent-friendly language reviewed by clinicians.
          </p>
        </div>
      </section>

      <section className="hc-container py-14 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="font-serif text-3xl font-semibold text-slate-900">What is a cochlear implant?</h2>
          <p className="mt-4 text-slate-700 leading-relaxed text-[17px]">
            A cochlear implant is a small electronic device that bypasses damaged hair cells in the
            inner ear and stimulates the hearing nerve directly. It restores access to sound for
            people with severe-to-profound hearing loss who do not get enough benefit from hearing aids.
          </p>
          <p className="mt-4 text-slate-700 leading-relaxed text-[17px]">
            A CI has two parts — an <strong>internal implant</strong> placed surgically under the skin
            behind the ear, and an <strong>external sound processor</strong> worn on or behind the ear.
          </p>
          <h3 className="mt-8 font-serif text-xl font-semibold text-slate-900">Important facts</h3>
          <ul className="mt-3 space-y-2">
            {[
              "CI is not 'brain surgery' — surgery is on the mastoid bone and cochlea, not the brain.",
              "Hearing after CI is restored gradually; therapy is critical for children.",
              "CI works alongside any hearing aid for the other ear.",
              "Modern implants are MRI safe up to 1.5T or 3.0T depending on the device.",
              "Three approved manufacturers serve India: Cochlear, MED-EL and Advanced Bionics.",
            ].map((t, i) => (
              <li key={i} className="flex gap-2 text-slate-700"><CheckCircle2 size={18} className="text-[#0F766E] mt-1 shrink-0" /> {t}</li>
            ))}
          </ul>
          <div className="mt-7">
            <Link to="/cochlear-implant-eligibility" className="btn-primary">Check if you may be a candidate <ArrowRight size={18} className="ml-1.5" /></Link>
          </div>
        </div>
        <img src={IMG.audiologist} alt="Audiologist with patient" className="rounded-3xl shadow-md" />
      </section>

      <section className="bg-slate-50 hc-section">
        <div className="hc-container">
          <h2 className="font-serif text-3xl font-semibold text-slate-900">When is a cochlear implant considered?</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {[
              ["Children with severe-to-profound hearing loss", "Particularly when hearing aids are not enabling speech development."],
              ["Adults whose hearing aids no longer help enough", "Frequent 'sorry, can you repeat?' moments despite well-fitted hearing aids."],
              ["Sudden or progressive hearing loss", "When hearing has deteriorated significantly."],
              ["Single-sided deafness in selected cases", "CI can restore directional hearing and reduce listening fatigue."],
              ["Post-meningitis hearing loss", "Early CI is critical before cochlear ossification sets in."],
              ["Congenital hearing loss with limited HA benefit", "Early identification + early CI gives best language outcomes."],
            ].map(([t, d], i) => (
              <div key={i} className="card-hc">
                <div className="font-serif text-lg font-semibold text-slate-900">{t}</div>
                <p className="mt-2 text-slate-600 text-[15px]">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
